import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { toast } from "@shared/ui/use-toast";
import { AlertTriangle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { CheckDetailData } from "./CheckDetailModal";
import { BUTTON_FIELD_CLASSES } from "@shared/utils/constants";
import { Button } from "@shared/ui/button";

import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import {
  useMarkChequeDishonoured,
  useUserNames,
  useChoices,
} from "@features/cx/paymentsv2/hooks";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  checkData: CheckDetailData;
  onDishonour: (data: any) => void;
}

export const DishonourModal: React.FC<Props> = ({
  isOpen,
  onClose,
  checkData,
  onDishonour,
}) => {
  const remoteUtilityId = getRemoteUtilityId();
  const markDishonouredMutation = useMarkChequeDishonoured(
    Number(remoteUtilityId),
  );

  const { data: userNamesData } = useUserNames(Number(remoteUtilityId));
  const { data: penaltyData } = useChoices("cheque_waiver_reason");
  const { data: bankReturnCodeData } = useChoices("nacha_return_code");

  const approverOptions = React.useMemo(() => {
    if (!userNamesData) return [];
    return userNamesData?.map((user: any) => ({
      label: user.name,
      value: user.name,
    }));
  }, [userNamesData]);

  const penaltyReasonOptions = React.useMemo(() => {
    if (!penaltyData) return [];
    return penaltyData?.map((penalty: any) => ({
      label: penalty.value,
      value: penalty.key,
    }));
  }, [penaltyData]);

  const bankReturnCodeOptions = React.useMemo(() => {
    if (!bankReturnCodeData) return [];
    return bankReturnCodeData?.map((bankReturnCode: any) => ({
      label: bankReturnCode.value,
      value: bankReturnCode.key,
    }));
  }, [bankReturnCodeData]);

  const [penaltyChoice, setPenaltyChoice] = useState("yes");
  const [penaltyAmount, setPenaltyAmount] = useState(25);

  // Auto-calculate penalty based on amount string (remove $ and commas)
  const amountValue = parseFloat(checkData.amount.replace(/[^0-9.-]+/g, ""));

  useEffect(() => {
    if (amountValue < 500) setPenaltyAmount(25);
    else if (amountValue < 1000) setPenaltyAmount(35);
    else if (amountValue < 5000) setPenaltyAmount(50);
    else setPenaltyAmount(75);
  }, [amountValue]);

  const formFields: FormField[] = [
    {
      name: "bankReturnCode",
      type: "select",
      label: "Bank Return Code",
      required: true,
      options: bankReturnCodeOptions,
      placeholder: "Select return reason",
      helperText: "Codes prefixed with R* follow NACHA ACH return conventions.",
      className: "col-span-2",
    },
    {
      name: "bankIntimationNotes",
      type: "textarea",
      label: "Bank Intimation Notes",
      required: false,
      placeholder: "Copied from the bank return memo, if any",
      className: "col-span-2",
    },
    {
      name: "penaltyChoice",
      type: "radio-card",
      label: "Apply a penalty charge for this dishonoured check?",
      required: true,
      options: [
        {
          label: "Yes — post penalty",
          value: "yes",
          description: "Post the SLA penalty charge to the consumer's account",
        },
        {
          label: "No — waive penalty",
          value: "no",
          description: "Waive the penalty charge with an approved reason",
        },
      ],
      classes: {
        container: "col-span-2 mb-2",
        radioGroup: "grid grid-cols-1 md:grid-cols-2 gap-4 w-full",
        radioCard:
          "flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all hover:border-blue-300 bg-white w-full",
        radioCardChecked:
          "border-blue-500 bg-blue-50/70 shadow-sm ring-1 ring-blue-500",
        description:
          "text-[12px] text-gray-500 mt-1 font-medium leading-normal",
      },
    },
    {
      name: "penaltyAmount",
      type: "number",
      label: "Penalty Amount",
      required: true,
      placeholder: "25.00",
      helperText:
        "Auto-calculated from slab. Override if approved by supervisor.",
      showWhen: (values: any) => values.penaltyChoice === "yes",
      className: "col-span-2",
    },
    {
      name: "waiverReason",
      type: "select",
      label: "Waiver Reason",
      required: true,
      options: penaltyReasonOptions,
      placeholder: "Select a waiver reason",
      showWhen: (values: any) => values.penaltyChoice === "no",
      className: "col-span-2",
    },
    {
      name: "approvedBy",
      type: "select",
      label: "Approved By",
      required: true,
      options: approverOptions,
      placeholder: "Select supervisor",
      className: "col-span-2",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    defaultValues: { penaltyChoice: "yes", penaltyAmount: penaltyAmount },
  });

  const handleFieldChange = (name: string, value: any) => {
    if (name === "penaltyChoice") {
      setPenaltyChoice(value);
    }
    if (name === "penaltyAmount") {
      setPenaltyAmount(parseFloat(value) || 0);
    }
  };

  const handleSubmit = (data: any) => {
    if (!data.bankReturnCode) {
      toast({
        title: "Error",
        description: "Select a return code.",
        variant: "destructive",
      });
      return;
    }
    if (!data.approvedBy) {
      toast({
        title: "Error",
        description: "Select an approver.",
        variant: "destructive",
      });
      return;
    }

    const isPenalty = penaltyChoice === "yes";

    const payload: any = {
      remote_utility_id: Number(remoteUtilityId),
      bank_return_code: data.bankReturnCode,
      post_penalty: penaltyChoice === "yes" ? true : false,
    };

    switch (penaltyChoice) {
      case "yes":
        payload.penalty_approved_by = data.approvedBy;
        break;
      case "no":
        payload.penalty_waiver_reason = data.waiverReason;
        payload.waiver_approved_by = data.approvedBy;
        break;
    }

    markDishonouredMutation.mutate(
      {
        chequeId: Number(checkData.id),
        payload,
      },
      {
        onSuccess: () => {
          if (isPenalty) {
            toast({
              title: "Dishonoured",
              description: `Check ${checkData.checkNo} dishonoured · $${amountValue.toFixed(2)} reversed · $${penaltyAmount.toFixed(2)} penalty posted`,
              variant: "destructive",
            });
          } else {
            toast({
              title: "Dishonoured",
              description: `Check ${checkData.checkNo} dishonoured · $${amountValue.toFixed(2)} reversed · penalty waived`,
              variant: "destructive",
            });
          }
          onDishonour(data);
        },
      },
    );
  };

  const renderNetImpact = () => {
    const totalImpact =
      amountValue + (penaltyChoice === "yes" ? penaltyAmount : 0);
    return (
      <div className="mt-6 p-4 rounded bg-red-50 border border-red-200">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center text-red-800 font-bold gap-2 md:gap-4">
          <span className="whitespace-nowrap text-sm">
            Net Impact to Consumer
          </span>
          <div className="text-base md:text-right break-words overflow-hidden">
            - ${totalImpact.toFixed(2)}
            <span className="text-xs font-normal ml-2 text-red-700">
              (reversal ${amountValue.toFixed(2)}{" "}
              {penaltyChoice === "yes"
                ? `+ penalty $${penaltyAmount.toFixed(2)}`
                : "· penalty waived"}
              )
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" /> Mark Check as Dishonoured
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pl-3 pr-3 py-2">
          <div className="p-4 rounded bg-amber-50 border border-amber-200 text-amber-800 text-sm mb-4">
            Dishonouring this check will: (1) reverse the provisional credit,
            (2) post a penalty charge, (3) lift the disconnect hold, and (4)
            notify the consumer.
          </div>

          <DynamicForm
            form={form}
            fields={formFields}
            onSubmit={handleSubmit}
            onFieldChange={handleFieldChange}
            config={{
              showSubmitButton: false,
              layout: "vertical",
              classes: BUTTON_FIELD_CLASSES,
            }}
            gridColumns={2}
            responsiveColumns={{ sm: 1, md: 2, lg: 2 }}
          />

          {renderNetImpact()}
        </div>

        <DialogFooter className="mt-4 pt-4 border-t flex justify-between sm:justify-between w-full">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={form.handleSubmit(handleSubmit)}
          >
            Post Reversal & Penalty
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
